const DEFAULT_API_BASE_URL = "https://api.miuphotobooth.com";

export function getGalleryCredentials(locationLike = window.location) {
  const params = new URLSearchParams(locationLike.search);
  const offline = locationLike.pathname.match(/^\/offline\/([a-f0-9]{32}-[a-f0-9]{32}|[A-Za-z0-9_-]{43})\/?$/);
  if (offline) return { token: offline[1], offline: true };
  const pathMatch = locationLike.pathname.match(/^\/g\/([^/]+)\/?$/);
  const siteMatch = locationLike.pathname.match(/^\/([a-z0-9][a-z0-9-]{1,46}[a-z0-9])\/g\/([^/]+)\/?$/);
  if (siteMatch) return { token: decodeURIComponent(siteMatch[2]), siteSlug: siteMatch[1] };
  return {
    token: pathMatch || locationLike.pathname === "/" ? params.get("token") || (pathMatch ? decodeURIComponent(pathMatch[1]) : "") : ""
  };
}

export function resolveApiBaseUrl(configuredValue) {
  const value = configuredValue && !configuredValue.includes("__PHOTO_API_BASE_URL__")
    ? configuredValue
    : DEFAULT_API_BASE_URL;
  return value.replace(/\/+$/, "");
}

export function galleryEndpoint(baseUrl, token, siteSlug) {
  if (siteSlug) return `${resolveApiBaseUrl(baseUrl)}/api/v3/gallery-sites/${encodeURIComponent(siteSlug)}/galleries/${encodeURIComponent(token)}`;
  return `${resolveApiBaseUrl(baseUrl)}/api/v3/gallery/${encodeURIComponent(token)}`;
}

export async function fetchGallery({ baseUrl, token, siteSlug, offline = false, signal, fetchImpl = fetch, timeoutMs = 15000 }) {
  if (!token) throw new GalleryError("missing_credentials");
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (signal?.aborted) cancel();
  else signal?.addEventListener("abort", cancel, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  try {
  let response;
  try {
    response = await fetchImpl(offline ? `${resolveApiBaseUrl(baseUrl)}/api/v3/offline-galleries/${encodeURIComponent(token)}` : galleryEndpoint(baseUrl, token, siteSlug), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
      credentials: "omit",
      referrerPolicy: "no-referrer"
    });
  } catch (error) {
    if (error?.name === "AbortError" && !timedOut) throw error;
    throw new GalleryError("network_error");
  }
  let body = {};
  try { body = await response.json(); } catch (error) {
    if (timedOut) throw new GalleryError("network_error");
    if (controller.signal.aborted) throw error;
    /* status remains authoritative */
  }
  const errorCode = typeof body.error === "object" ? body.error?.code : body.error;
  if (!response.ok) throw new GalleryError(errorCode || "request_failed", response.status);
  if (!body.gallery || !Array.isArray(body.gallery.assets)) throw new GalleryError("invalid_response");
  return body.gallery;
  } finally { clearTimeout(timer); signal?.removeEventListener("abort", cancel); }
}

export class GalleryError extends Error {
  constructor(code, status = 0) {
    super(code);
    this.name = "GalleryError";
    this.code = code;
    this.status = status;
  }
}

export async function fetchSelfBooth({ token, page = 1, baseUrl, signal, fetchImpl = fetch }) {
  const response = await fetchImpl(`${resolveApiBaseUrl(baseUrl)}/api/v3/selfbooth/${encodeURIComponent(token)}?page=${page}`, {
    credentials: "omit", referrerPolicy: "no-referrer", signal, headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new GalleryError(response.status === 404 ? "selfbooth_not_found" : "request_failed", response.status);
  const result = await response.json();
  if (!Array.isArray(result.galleries)) throw new GalleryError("invalid_response");
  return result;
}
